import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, BarChart3, FileText, MessagesSquare, X, CreditCard, Bell, FolderDown, Send, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const STAGES = ["concept", "revision", "validation", "delivered"];

const StatusBadge = ({ value, t }) => {
  const map = {
    paid: "bg-emerald-100 text-emerald-800",
    due: "bg-amber-100 text-amber-800",
    open: "bg-[#B8933F]/15 text-[#8a6d1f]",
    in_progress: "bg-blue-100 text-blue-800",
    resolved: "bg-emerald-100 text-emerald-800",
  };
  const labels = { paid: t.portal.paid, due: t.portal.dueStatus, open: t.portal.open, in_progress: t.portal.inProgress, resolved: t.portal.resolved };
  return <span className={`inline-block px-2.5 py-1 text-xs font-medium ${map[value] || "bg-gray-100 text-gray-700"}`}>{labels[value] || value}</span>;
};

export default function ClientPortal() {
  const { t } = useLang();
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(false);
  const [req, setReq] = useState({ kind: "request", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [msgText, setMsgText] = useState("");
  const { content } = useContent();

  const load = useCallback(() => Promise.all([
    api.get("/client/dashboard").then(({ data }) => setData(data)),
    api.get("/client/messages").then(({ data }) => {
      setMessages(data);
      setUnread(data.filter((m) => m.sender === "admin" && !m.read_by_client).length);
    }),
  ]), []);

  useEffect(() => {
    document.title = "Client Portal — Bezalel Visionary";
    if (!checking && user === false) navigate("/portal/login");
    if (user && user.role === "admin") navigate("/admin");
    if (user && user.role === "client") load();
  }, [user, checking]); // eslint-disable-line

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!req.subject.trim()) return;
    setSending(true);
    try {
      await api.post("/client/requests", req);
      setModal(false);
      setReq({ kind: "request", subject: "", message: "" });
      setSent(true);
      setTimeout(() => setSent(false), 4000);
      load();
    } finally {
      setSending(false);
    }
  };

  const payInvoice = async (id) => {
    const { data } = await api.post(`/client/invoices/${id}/checkout`, { origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  };

  const downloadReceipt = async (id, number) => {
    const res = await api.get(`/client/invoices/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msgText.trim()) return;
    await api.post("/client/messages", { body: msgText });
    setMsgText("");
    api.get("/client/messages").then(({ data }) => { setMessages(data); setUnread(0); });
  };

  if (checking || !data) {
    return <div className="min-h-screen bg-[#F7F3EC] flex items-center justify-center text-[#9A9184]" data-testid="portal-loading">{t.misc.loading}</div>;
  }

  const project = data.project;
  const stageIdx = project ? STAGES.indexOf(project.status) : -1;

  return (
    <main className="min-h-screen bg-[#F7F3EC]" data-testid="client-portal-page">
      <header className="bg-[#191713] px-6 lg:px-10 h-16 flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-5">
          <span className="hidden sm:block text-sm text-[#9A9184]">{data.user.business_name || data.user.name}</span>
          <button data-testid="portal-logout-button" onClick={async () => { await logout(); navigate("/"); }}
            className="flex items-center gap-2 text-sm text-[#F7F3EC]/80 hover:text-[#B8933F] transition-colors">
            <LogOut size={15} /> {t.portal.logout}
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <p className="eyebrow-label">{t.portal.welcome}</p>
        <h1 className="mt-3 font-serif text-3xl sm:text-4xl text-[#191713]" data-testid="portal-welcome-name">{data.user.name}</h1>
        {sent && <p className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-2.5 inline-block" data-testid="portal-request-sent">{t.portal.requestSent}</p>}

        {project?.updates?.length > 0 && (
          <div className="mt-6 bg-[#B8933F]/10 border border-[#B8933F]/40 px-5 py-4 flex items-start gap-3" data-testid="portal-updates-banner">
            <Bell size={16} className="text-[#A6853B] mt-0.5 shrink-0" />
            <div>
              <p className="text-[0.65rem] tracking-[0.2em] uppercase font-semibold text-[#A6853B]">{t.portal2.updates}</p>
              <p className="mt-1 text-sm text-[#191713]">{project.updates[project.updates.length - 1].text}</p>
              <p className="mt-1 text-xs text-[#8A8378]">{new Date(project.updates[project.updates.length - 1].at).toLocaleDateString()}</p>
            </div>
          </div>
        )}

        {/* Project status */}
        <section className="mt-10 bg-white border border-[#191713]/10 p-6 lg:p-8" data-testid="portal-project-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-serif text-xl text-[#191713]">{t.portal.project}</h2>
            {project && <span className="text-sm text-[#9A9184]">{project.name}</span>}
          </div>
          {project ? (
            <div className="mt-8">
              <div className="flex items-center">
                {STAGES.map((s, i) => (
                  <div key={s} className="flex items-center flex-1 last:flex-none">
                    <div className="flex flex-col items-center gap-2">
                      <span className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${
                        i <= stageIdx ? "bg-[#B8933F] border-[#B8933F] text-[#191713]" : "border-[#191713]/15 text-[#9A9184] bg-white"
                      }`} data-testid={`portal-stage-${s}`}>{i + 1}</span>
                      <span className={`text-[0.65rem] tracking-[0.12em] uppercase whitespace-nowrap ${i <= stageIdx ? "text-[#191713] font-semibold" : "text-[#9A9184]"}`}>
                        {t.portal.stages[s]}
                      </span>
                    </div>
                    {i < STAGES.length - 1 && <span className={`h-px flex-1 mx-2 mb-6 ${i < stageIdx ? "bg-[#B8933F]" : "bg-[#191713]/10"}`} />}
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-[#9A9184]">{t.portal.updated}: {new Date(project.updated_at).toLocaleDateString()}</p>
            </div>
          ) : (
            <p className="mt-5 text-sm text-[#9A9184]">—</p>
          )}
        </section>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Visibility */}
          <section className="bg-white border border-[#191713]/10 p-6 lg:p-8" data-testid="portal-visibility-section">
            <h2 className="font-serif text-xl text-[#191713] flex items-center gap-3"><BarChart3 size={18} className="text-[#B8933F]" /> {t.portal.visibility}</h2>
            {data.visibility ? (
              <div className="mt-5" />
            ) : (
              <div className="mt-6 border border-dashed border-[#191713]/20 p-6 text-center">
                <p className="text-sm text-[#9A9184] leading-relaxed">{t.portal.visibilityEmpty}</p>
              </div>
            )}
          </section>

          {/* Finance */}
          <section className="bg-white border border-[#191713]/10 p-6 lg:p-8" data-testid="portal-finance-section">
            <h2 className="font-serif text-xl text-[#191713] flex items-center gap-3"><FileText size={18} className="text-[#B8933F]" /> {t.portal.finance}</h2>
            <div className="mt-5 divide-y divide-[#191713]/8">
              {data.invoices.map((inv) => (
                <div key={inv.id} className="py-3.5 flex items-center justify-between gap-3" data-testid={`portal-invoice-${inv.number}`}>
                  <div>
                    <p className="text-sm font-medium text-[#191713]">{inv.number}</p>
                    <p className="text-xs text-[#9A9184] mt-0.5">{inv.label}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-[#191713]">{inv.amount_aed} AED</p>
                    <StatusBadge value={inv.status} t={t} />
                    {inv.status === "due" && (
                      <button data-testid={`portal-pay-invoice-${inv.number}`} onClick={() => payInvoice(inv.id)}
                        className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#F7F3EC] bg-[#191713] hover:bg-[#2C2822] transition-colors px-3 py-2 ml-auto">
                        <CreditCard size={12} /> {t.portal2.payNow}
                      </button>
                    )}
                    {inv.status === "paid" && (
                      <button data-testid={`portal-receipt-${inv.number}`} onClick={() => downloadReceipt(inv.id, inv.number)}
                        className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#A6853B] border border-[#A6853B]/40 hover:border-[#A6853B] transition-colors px-3 py-2 ml-auto">
                        <FileText size={12} /> {t.portal2.receipt}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Messages */}
          <section className="bg-white border border-[#191713]/10 p-6 lg:p-8 flex flex-col" data-testid="portal-messages-section">
            <h2 className="font-serif text-xl text-[#191713] flex items-center gap-3">
              <Mail size={18} className="text-[#A6853B]" /> {t.portal2.messages}
              {unread > 0 && (
                <span className="bg-[#A6853B] text-white text-[0.65rem] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="portal-unread-badge">{unread}</span>
              )}
            </h2>
            <div className="mt-5 flex-1 max-h-72 overflow-y-auto space-y-3 pr-1" data-testid="portal-messages-list">
              {messages.length === 0 ? (
                <p className="text-sm text-[#8A8378]">{t.portal2.noMessages}</p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`max-w-[85%] px-4 py-3 text-sm ${m.sender === "client" ? "ml-auto bg-[#191713] text-[#F7F3EC]" : "bg-[#F7F3EC] text-[#191713] border border-[#191713]/10"}`}>
                    <p>{m.body}</p>
                    <p className={`mt-1 text-[0.6rem] ${m.sender === "client" ? "text-[#9A9184]" : "text-[#8A8378]"}`}>{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                ))
              )}
            </div>
            <form onSubmit={sendMessage} className="mt-4 flex gap-2">
              <input data-testid="portal-message-input" className="field-input flex-1" placeholder={t.portal2.typeMessage}
                value={msgText} onChange={(e) => setMsgText(e.target.value)} />
              <button type="submit" data-testid="portal-message-send" className="btn-dark !py-2.5 !px-4" aria-label={t.portal2.send}>
                <Send size={15} />
              </button>
            </form>
          </section>

          {/* Deliverables */}
          <section className="bg-white border border-[#191713]/10 p-6 lg:p-8" data-testid="portal-deliverables-section">
            <h2 className="font-serif text-xl text-[#191713] flex items-center gap-3"><FolderDown size={18} className="text-[#A6853B]" /> {t.portal2.deliverables}</h2>
            {(project?.deliverables || []).length === 0 ? (
              <p className="mt-6 text-sm text-[#8A8378]">{t.portal2.noDeliverables}</p>
            ) : (
              <div className="mt-5 divide-y divide-[#191713]/8">
                {project.deliverables.map((d) => (
                  <div key={d.id} className="py-3.5 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-[#191713]">{d.title}</p>
                      <p className="text-xs text-[#8A8378] mt-0.5">{new Date(d.at).toLocaleDateString()}</p>
                    </div>
                    {d.url && (
                      <a href={d.url} target="_blank" rel="noreferrer" className="text-xs tracking-[0.12em] uppercase text-[#A6853B] hover:text-[#191713] transition-colors">
                        {t.portal2.open}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="mt-8 pt-6 border-t border-[#191713]/10" data-testid="portal-contact-box">
              <p className="text-[0.65rem] tracking-[0.2em] uppercase font-semibold text-[#A6853B]">{t.portal2.contactTitle}</p>
              <p className="mt-2 text-sm text-[#191713]/65">{t.portal2.contactBody}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                {content?.contact?.whatsapp && (
                  <a href={`https://wa.me/${content.contact.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
                    data-testid="portal-whatsapp-link" className="btn-outline-dark !py-2 !px-4 text-xs">{t.portal2.whatsapp}</a>
                )}
                {content?.contact?.email && (
                  <a href={`mailto:${content.contact.email}`} data-testid="portal-email-link" className="btn-outline-dark !py-2 !px-4 text-xs">{t.portal2.emailUs}</a>
                )}
                {!content?.contact?.whatsapp && !content?.contact?.email && (
                  <p className="text-xs text-[#8A8378] italic">—</p>
                )}
              </div>
            </div>
          </section>
        </div>

        {/* Requests */}
        <section className="mt-6 bg-white border border-[#191713]/10 p-6 lg:p-8" data-testid="portal-requests-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-serif text-xl text-[#191713] flex items-center gap-3"><MessagesSquare size={18} className="text-[#B8933F]" /> {t.portal.requests}</h2>
            <button data-testid="portal-new-request-button" onClick={() => setModal(true)} className="btn-outline-dark !py-2 !px-4 text-xs">
              <Plus size={14} /> {t.portal.newRequest}
            </button>
          </div>
          {data.requests.length === 0 ? (
            <p className="mt-6 text-sm text-[#9A9184]">{t.portal.empty}</p>
          ) : (
            <div className="mt-5 divide-y divide-[#191713]/8">
              {data.requests.map((r) => (
                <div key={r.id} className="py-4 flex items-start justify-between gap-4" data-testid={`portal-request-item-${r.id}`}>
                  <div>
                    <p className="text-sm font-medium text-[#191713]">
                      <span className="text-[0.6rem] tracking-[0.15em] uppercase text-[#B8933F] mr-2">{r.kind === "revision" ? t.portal.revision : t.portal.request}</span>
                      {r.subject}
                    </p>
                    <p className="text-xs text-[#9A9184] mt-1 max-w-lg">{r.message}</p>
                  </div>
                  <StatusBadge value={r.status} t={t} />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Request modal */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-[#191713]/70 flex items-center justify-center px-6" role="dialog" aria-modal="true" data-testid="portal-request-modal">
          <form onSubmit={submitRequest} className="bg-white w-full max-w-md p-8">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl text-[#191713]">{t.portal.newRequest}</h3>
              <button type="button" onClick={() => setModal(false)} aria-label="Close" data-testid="portal-modal-close"><X size={18} /></button>
            </div>
            <div className="mt-6 space-y-4">
              <div>
                <label className="field-label" htmlFor="req-kind">{t.portal.kind}</label>
                <select id="req-kind" data-testid="portal-request-kind" className="field-input" value={req.kind} onChange={(e) => setReq({ ...req, kind: e.target.value })}>
                  <option value="request">{t.portal.request}</option>
                  <option value="revision">{t.portal.revision}</option>
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="req-subject">{t.portal.subject}</label>
                <input id="req-subject" data-testid="portal-request-subject" className="field-input" value={req.subject} onChange={(e) => setReq({ ...req, subject: e.target.value })} required />
              </div>
              <div>
                <label className="field-label" htmlFor="req-message">{t.portal.messageLabel}</label>
                <textarea id="req-message" rows={4} data-testid="portal-request-message" className="field-input resize-none" value={req.message} onChange={(e) => setReq({ ...req, message: e.target.value })} />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button type="submit" data-testid="portal-request-submit" disabled={sending} className="btn-gold flex-1 disabled:opacity-60">{t.portal.send}</button>
              <button type="button" onClick={() => setModal(false)} className="btn-outline-dark">{t.portal.cancel}</button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}
